import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AiCategory } from '@b4b/shared';
import { useSettings, useTopics, useUpdateSettings } from '../../api/queries.js';
import ClusterCard from '../ClusterCard.js';
import FeedFooter from './FeedFooter.js';
import FilterChips from './FilterChips.js';
import { useCardKeyboard } from '../../lib/useCardKeyboard.js';
import { feedUrl, type FeedParams } from '../../lib/feedUrl.js';
import { useTopicLabel } from '../../lib/topics.js';

// Apple News "Today" style: one section per AI category with the newest stories
// and a "N more →" link that drills into a flat list filtered by the topic.
export default function TopicsFeed({
  params,
  fetchingFirstArticles,
}: {
  params: FeedParams;
  fetchingFirstArticles: boolean;
}) {
  const { t } = useTranslation();
  const topicLabel = useTopicLabel();
  const topics = useTopics(params.filter, params.category);
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const hidden = settings.data?.hiddenTopics ?? [];

  const sections = useMemo(
    () => (topics.data?.sections ?? []).filter((section) => !hidden.includes(section.topic)),
    [topics.data, hidden],
  );
  const allItems = useMemo(() => sections.flatMap((section) => section.items), [sections]);
  const focusIndex = useCardKeyboard(allItems);

  const hideTopic = (topic: AiCategory) =>
    updateSettings.mutate({ hiddenTopics: [...hidden.filter((item) => item !== topic), topic] });

  if (topics.isLoading) {
    return <p className="py-24 text-center text-neutral-500">{t('common.loading')}</p>;
  }
  if (topics.isError) {
    return (
      <p className="py-24 text-center text-neutral-500">
        {t('common.error')}{' '}
        <button onClick={() => void topics.refetch()} className="text-accent underline">
          {t('common.retry')}
        </button>
      </p>
    );
  }

  let offset = 0;
  return (
    <div data-layout="topics" className="px-4 pb-4 pt-20">
      <FilterChips params={params} />
      <div className="space-y-8">
        {sections.map((section) => {
          const start = offset;
          offset += section.items.length;
          const remaining = section.count - section.items.length;
          return (
            <section key={section.topic} data-topic={section.topic}>
              <div className="mb-2 flex items-baseline gap-2">
                <h2 className="text-lg font-bold">{topicLabel(section.topic)}</h2>
                <span className="text-xs text-neutral-400">{t('feed.topicCount', { count: section.count })}</span>
                <button
                  onClick={() => hideTopic(section.topic)}
                  className="ml-auto flex min-h-[44px] items-center rounded-full px-3 text-xs text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
                >
                  {t('feed.hideTopic')} ✕
                </button>
              </div>
              <div className="space-y-3">
                {section.items.map((item, index) => (
                  <ClusterCard key={item.id} item={item} focused={start + index === focusIndex} imageSize="lg" />
                ))}
              </div>
              {remaining > 0 && (
                <Link
                  to={feedUrl({ ...params, topic: section.topic })}
                  className="mt-2 flex min-h-[44px] items-center justify-end px-1 text-sm font-medium text-accent"
                >
                  {t('feed.moreInTopic', { count: remaining })}
                </Link>
              )}
            </section>
          );
        })}
      </div>
      {/* sections already say "N more →" — "all done" only when nothing is left */}
      <FeedFooter
        itemCount={allItems.length}
        params={params}
        fetchingFirstArticles={fetchingFirstArticles && sections.length === 0}
        showAllDone={sections.length === 0}
      />
    </div>
  );
}
